import { hasSensitiveParams } from '@/utils/consent'

export default defineNuxtPlugin(() => {
    // GTM loads in EVERY environment, staging included — decided 2026-09-03.
    //
    // This plugin previously did nothing at all, anywhere, for two independent
    // reasons. Both were silent, which is why the container looked wired up:
    //
    //  1. It was gated on `process.env.NODE_ENV === 'production'`. This is a
    //     .client plugin, so that runs in the BROWSER, where the Cloudflare
    //     Workers build has no `process` — the check was always falsy and GTM
    //     was skipped even on production.
    //
    //  2. It destructured `const { $script } = useScriptGoogleTagManager(...)`.
    //     That was an older @nuxt/scripts API. As of 1.3.3 the composable
    //     returns UseScriptContext directly — the script instance itself, with
    //     load()/status on it and no `$script` property. So `$script` was
    //     undefined and the `if ($script && ...)` guard short-circuited without
    //     error.
    //
    // Verified before the fix: on ohc.architecture.org AND staging, no
    // googletagmanager script was ever requested and `window.google_tag_manager`
    // was an empty object. The only thing present was the `gtm.start` dataLayer
    // push that @nuxt/scripts makes regardless, which is what made it look live.
    //
    // load() is required because nuxt.config sets
    // `scripts.defaultScriptOptions.trigger: 'manual'` project-wide, and manual
    // means the script is not fetched until load() is called. That default is
    // deliberate and left alone.
    //
    // Any per-environment difference belongs in deploy config, NOT in a
    // `process.env.NODE_ENV` check here: this is a .client plugin, and that
    // check is what silently disabled GTM in every environment for months
    // (reason 1 above).
    // The container id comes from NUXT_PUBLIC_GTM_ID. Staging AND production
    // both load GTM-PV5K3W, CAC's container that architecture.org also uses;
    // production gets it from the GitHub NUXT_PUBLIC_GTM_ID secret. GTM-TQSGP5,
    // which older comments here and in deploy config named as production's
    // container, was never meant to be used — do not switch to it, or CAC's
    // tags (CookieYes included) disappear from OHC. `gtmId` had been declared
    // in runtimeConfig since this plugin was written but nothing read it: the
    // id was hardcoded here.
    //
    // The .trim() below is load-bearing: the production secret has been seen
    // with a trailing space.
    //
    // No id, no GTM. There is deliberately NO default container: an environment
    // that has not been told where to report should send nothing, rather than
    // quietly pushing its traffic into whichever container was hardcoded here.
    // That is what local dev was doing. It also means a missing or misspelt env
    // var shows up as "no analytics at all", which someone notices, instead of
    // data landing in the wrong property, which nobody does.
    const config = useRuntimeConfig()
    const containerId = String(config.public?.gtmId || '').trim()

    if (!containerId) return

    // Never load GTM on a URL that carries a credential. The magic-link sign-in
    // page arrives with ?token=, and GTM's page_location (and every tag that
    // reads the URL) would send it to Google and CAC's other vendors. That page
    // redirects straight away, and GTM loads normally on the page it lands on.
    if (hasSensitiveParams(window.location.href)) return

    try {
        const gtm = useScriptGoogleTagManager({
            id: containerId,
        })

        // Fire load(), do NOT await it. nuxt.config sets
        // `scripts.defaultScriptOptions.trigger: 'manual'` project-wide, so the
        // container is not fetched until load() is called — but the promise it
        // returns resolves only once the script's API is available, and it was
        // observed never resolving here (status stayed `awaitingLoad`). Awaiting
        // it inside an async plugin stalls the plugin, which is worse than the
        // original bug. Nothing downstream needs the return value: GTM's own
        // dataLayer queue buffers anything pushed before it finishes loading.
        gtm?.load?.()
    } catch (error) {
        // Analytics must never break the page.
        console.error('Error loading GTM:', error)
    }
})
